import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useValuationParams } from '../../hooks/useValuationParams';
import { APP_VERSION } from '../../version';
import { t } from '../../i18n/t';
import './StatusFooter.css';
import { formatDateTimeIL } from '../../utils/dateTime';

// Background keyed on the config-driven `environmentKind` (prod|test|dev —
// see AppOptions.EnvironmentKind on the backend), NOT the raw ASP.NET Core
// environment name. `prod` intentionally has no entry (no highlight).
const KIND_BACKGROUNDS: Record<string, string> = {
  dev: 'var(--footer-dev-bg)',
  test: 'var(--footer-test-bg)',
};

// The valuation type whose day gets the "(כספים)" marker. Matched on the stable
// English CODE from Central ValuationTypes, never on the id — the ids were remapped
// once already (central V1.71.2 moved finance from 2 to 3).
const FINANCE_TYPE_CODE = 'finance';

function formatNow(d: Date): string {
  return formatDateTimeIL(d, { seconds: true });
}

// The API serves a date-only value as 'yyyy-MM-dd'. Reformat the string directly
// rather than via `new Date(iso)`, which parses it as UTC midnight and can render
// the previous day for users west of Greenwich.
function formatIsoDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

// `date` null = day-open has not set this one yet — say so explicitly rather than
// leaving the slot blank, which reads as "no such thing" instead of "not set".
function ValuationDate({ label, date, typeCode }: {
  label: string;
  date: string | null;
  typeCode: string | null;
}) {
  const isFinance = typeCode === FINANCE_TYPE_CODE;
  return (
    <span className="status-footer__valuation-item">
      <span className="status-footer__valuation-label">{label}:</span>
      {date ? (
        <span className="status-footer__valuation-value">
          {formatIsoDate(date)}
          {isFinance && <span className="status-footer__valuation-finance"> ({t('statusFooter.financeDay')})</span>}
        </span>
      ) : (
        <span className="status-footer__valuation-value status-footer__valuation-value--empty">
          {t('statusFooter.noValuationDate')}
        </span>
      )}
    </span>
  );
}

export function StatusFooter() {
  const { user } = useAuth();
  const [now, setNow] = useState<Date>(() => new Date());
  // Subscribed to the shared cache: a day-open elsewhere in the app calls
  // refreshValuationParams() and the footer re-renders.
  const { data: valuation } = useValuationParams();

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!user) return null;

  const background = KIND_BACKGROUNDS[user.environmentKind];
  const style = background ? { background } : undefined;
  const nickname = user.environmentNickname?.trim();

  return (
    <footer className="status-footer" style={style}>
      <span className="status-footer__db">
        {user.centralDbName ?? ''}
        <span className="status-footer__version">VER {APP_VERSION}</span>
        {nickname && (
          <span className="status-footer__env">
            {t('statusFooter.env')}: {nickname}
          </span>
        )}
      </span>
      {/* Rendered only once loaded — a failed fetch shows nothing rather than a
          placeholder that would be indistinguishable from "not set". */}
      {valuation && (
        <span className="status-footer__valuation">
          <ValuationDate
            label={t('statusFooter.israeliValuationDate')}
            date={valuation.israeliValuationDate}
            typeCode={valuation.israeliValuationTypeCode}
          />
          <ValuationDate
            label={t('statusFooter.foreignValuationDate')}
            date={valuation.foreignValuationDate}
            typeCode={valuation.foreignValuationTypeCode}
          />
        </span>
      )}
      <span className="status-footer__clock">{formatNow(now)}</span>
    </footer>
  );
}
